import { includes } from '../../../utils/array/array';
import {
  makeBoxRetailUnitRoleName,
  makeFilterRetailUnitRoleName,
  makeRetailUnitRoleName,
  ROLE_GLOBAL,
} from '../../../utils/roles/roles';
import { createReferenceEqualSelector } from '../utils';
import { selectCurrentRetailUnit } from '../../slices/intl';
import { selectRoles } from '../../slices/roles';
import type { RootState } from '../..';

export const selectHasRoles = createReferenceEqualSelector(
  selectRoles,
  (_, roles) => roles,
  (userProfileRoles, roles) => includes(userProfileRoles, roles),
);

export const selectHasGlobalEditorRole = createReferenceEqualSelector(
  (state) => state.roles,
  (roles) => roles.includes(ROLE_GLOBAL),
);

export const selectHasRetailUnitRole = (state: RootState, retailUnit: string) =>
  selectHasRoles(state, [makeRetailUnitRoleName(retailUnit)]);

export const selectHasFilterRetailUnitRole = (
  state: RootState,
  retailUnit: string,
) => selectHasRoles(state, [makeFilterRetailUnitRoleName(retailUnit)]);

export const selectIsEditorForCurrentMarket = createReferenceEqualSelector(
  selectRoles,
  selectCurrentRetailUnit,
  (userProfileRoles, retailUnit) =>
    !!retailUnit && includes(userProfileRoles, [makeRetailUnitRoleName(retailUnit)]),
);

export const selectIsBoxEditorForCurrentMarket = createReferenceEqualSelector(
  selectRoles,
  selectCurrentRetailUnit,
  (userProfileRoles, retailUnit) =>
    includes(userProfileRoles, [makeBoxRetailUnitRoleName(retailUnit)]),
);

export const selectIsStoreEditorForCurrentMarket = createReferenceEqualSelector(
  selectRoles,
  selectCurrentRetailUnit,
  (userProfileRoles) => includes(userProfileRoles, ['RC_STORE_ZZ']),
);

const ROLE_IKEA_USER = 'RC_IKEA_USER';
const FILTERS_ROLE_PREFIX = 'RC_FILTERS_';

export type LandingPage = 'catalog' | 'indexable';

/**
 * Users with only filter roles (besides the default RC_IKEA_USER) land on
 * indexable filters; everyone else lands on the catalog.
 */
export const selectLandingPage = createReferenceEqualSelector(
  selectRoles,
  (roles): LandingPage => {
    const isFilterRole = (role: string) => role.startsWith(FILTERS_ROLE_PREFIX);
    const isFiltersOnlyUser =
      roles.some(isFilterRole) &&
      roles.every((role) => role === ROLE_IKEA_USER || isFilterRole(role));

    return isFiltersOnlyUser ? 'indexable' : 'catalog';
  },
);
